/**
 * Offline-Warteschlange für „Flug starten“, „Flug beenden“ und die Nachflug-Angaben (Vorkommnisse).
 *
 * - Schlägt eine Anfrage fehl, bleibt die Aktion erhalten (Netzwerkfehler/Timeout/5xx/401/403: Backoff + Wiederholung, rot angezeigt,
 *   Verwerfen nur durch den Nutzer). Nur eindeutige Ablehnungen 400/404/409/410/413/422 entfernen sie – mit sichtbarer Meldung samt Inhalt.
 * - (Ursprünglich:) Schlägt eine Anfrage wegen fehlender Verbindung fehl (kein Antwort/Netzwerkfehler/Timeout/502-504),
 *   wird die Aktion lokal gespeichert (localStorage, überlebt Reload) und später in Reihenfolge nachgesendet.
 * - Zeitstempel = Zeitpunkt der Erfassung (startzeit/endzeit werden mitgeschickt, nicht der Sendezeitpunkt).
 * - Doppelte Flüge verhindert eine clientseitige UUID (fluege.client_uuid, eindeutig): gleiche UUID -> die API liefert den vorhandenen Flug.
 * - Enden/Updates verweisen auf einen Flug entweder per id (Flug existiert schon) oder per UUID (Start noch in der Warteschlange).
 */
import { api } from '@pilot-hub/api-client'

const KEY = 'pilot-hub-flug-queue-v1'
const FEHLER_KEY = 'pilot-hub-flug-queue-fehler-v1'
const TIMEOUT_MS = 12000

export interface FlugRef { id?: number; uuid?: string }

export type QueueEintrag = (
  | { typ: 'start'; qid: string; nutzerId: number; erfasstAm: string; uuid: string; payload: Record<string, unknown>; anzeige: StartAnzeige }
  | { typ: 'ende'; qid: string; nutzerId: number; erfasstAm: string; ref: FlugRef; daten: Record<string, unknown> }
  | { typ: 'update'; qid: string; nutzerId: number; erfasstAm: string; ref: FlugRef; daten: Record<string, unknown> }
) & QueueStatus

/** Für die lokale Anzeige eines noch nicht gesendeten Fluges */
export interface StartAnzeige { drohne_label?: string | null; akku_label?: string | null; einsatz_titel?: string | null; einsatz_id?: number | null }

export interface QueueFehler { at: string; text: string }

/** Zustand eines Eintrags, der wiederholt nicht gesendet werden konnte (bleibt IMMER erhalten, bis er gesendet oder vom Nutzer verworfen wird) */
export interface QueueStatus { versuche?: number; nichtVor?: number; letzterFehler?: string }

/** Eindeutige inhaltliche Ablehnungen des Servers: Wiederholen ist sinnlos. Alles andere (500, 401, 403, 408, 429 ...) bleibt in der Warteschlange. */
const ENDGUELTIG_ABGELEHNT = new Set([400, 404, 409, 410, 413, 422])
export function istEndgueltigAbgelehnt(err: any): boolean {
  const status: number | undefined = err?.response?.status
  return status !== undefined && ENDGUELTIG_ABGELEHNT.has(status)
}

/** Verbindungsproblem (-> in die Warteschlange) im Unterschied zu einer inhaltlichen Ablehnung (4xx) */
export function istVerbindungsfehler(err: any): boolean {
  if (err?.code === 'ERR_CANCELED') return false
  const status: number | undefined = err?.response?.status
  if (status === undefined) return true // keine Antwort: Netzwerkfehler oder Timeout
  return status === 502 || status === 503 || status === 504
}

export function neueUuid(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  } catch { /* unsicherer Kontext (http): Fallback */ }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

/** Lokale Wanduhrzeit als ISO ohne Zeitzone (wie die API sie speichert), z. B. 2026-10-28T07:41:05 */
export function lokaleZeit(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

// ---------------------------------------------------------------------------
// Speicher
// ---------------------------------------------------------------------------
const listeners = new Set<() => void>()
let cache: QueueEintrag[] | null = null

function lesen(): QueueEintrag[] {
  if (cache) return cache
  try {
    const roh = localStorage.getItem(KEY)
    cache = roh ? (JSON.parse(roh) as QueueEintrag[]) : []
  } catch { cache = [] }
  return cache
}

function schreiben(liste: QueueEintrag[]) {
  cache = liste
  try { localStorage.setItem(KEY, JSON.stringify(liste)) } catch { /* Speicher voll/gesperrt: im Arbeitsspeicher weiter */ }
  listeners.forEach(l => l())
}

if (typeof window !== 'undefined') {
  // Änderungen aus einem anderen Tab übernehmen
  window.addEventListener('storage', e => {
    if (e.key === KEY) { cache = null; listeners.forEach(l => l()) }
  })
}

export function queueLesen(): QueueEintrag[] { return lesen() }
export function queueAbonnieren(l: () => void): () => void { listeners.add(l); return () => { listeners.delete(l) } }

export function queueHinzufuegen(e: QueueEintrag) { schreiben([...lesen(), e]) }

export function neueQid(): string { return neueUuid() }

export function fehlerLesen(): QueueFehler[] {
  try { return JSON.parse(localStorage.getItem(FEHLER_KEY) || '[]') as QueueFehler[] } catch { return [] }
}
export function fehlerLeeren() {
  try { localStorage.removeItem(FEHLER_KEY) } catch { /* egal */ }
  cache = [...lesen()] // neuer Snapshot, damit abonnierte Komponenten neu rendern
  listeners.forEach(l => l())
}
function fehlerMerken(text: string) {
  try { localStorage.setItem(FEHLER_KEY, JSON.stringify([...fehlerLesen(), { at: lokaleZeit(), text }].slice(-10))) } catch { /* egal */ }
}

// ---------------------------------------------------------------------------
// Abgeleitete Sicht für die Anzeige
// ---------------------------------------------------------------------------
export interface LokalerFlug {
  uuid: string
  startzeit: string
  anzeige: StartAnzeige
  /** bereits lokal beendet (Ende wartet auf Versand) */
  beendet: boolean
  nutzerId: number
}

/** Noch nicht gesendete Starts eines Nutzers (laufend oder lokal bereits beendet) */
export function lokaleFluege(nutzerId: number): LokalerFlug[] {
  const liste = lesen()
  return liste
    .filter((e): e is Extract<QueueEintrag, { typ: 'start' }> => e.typ === 'start' && e.nutzerId === nutzerId)
    .map(s => ({
      uuid: s.uuid,
      startzeit: String(s.payload.startzeit ?? s.erfasstAm),
      anzeige: s.anzeige,
      nutzerId: s.nutzerId,
      beendet: liste.some(e => e.typ === 'ende' && e.ref.uuid === s.uuid),
    }))
}

/** Ids bereits (offline) beendeter, aber serverseitig noch laufender Flüge */
export function lokalBeendeteIds(nutzerId: number): Set<number> {
  const ids = new Set<number>()
  for (const e of lesen()) if (e.typ === 'ende' && e.nutzerId === nutzerId && e.ref.id !== undefined) ids.add(e.ref.id)
  return ids
}

// ---------------------------------------------------------------------------
// Beschreibung / Verwerfen (nur durch den Nutzer)
// ---------------------------------------------------------------------------
function zeitText(z: unknown): string { return typeof z === 'string' ? z.replace('T', ' ').slice(0, 16) : '' }

/** Lesbare Beschreibung einer Aktion mit Drohne und Zeit, z. B. „Flug starten (Drohne M30T, 2026-10-28 07:41)“ */
export function beschreibe(e: QueueEintrag, liste: QueueEintrag[] = lesen()): string {
  const start = e.typ === 'start' ? e : liste.find((x): x is Extract<QueueEintrag, { typ: 'start' }> => x.typ === 'start' && x.uuid === e.ref.uuid)
  const drohne = start?.anzeige.drohne_label
  const was = e.typ === 'start' ? 'Flug starten' : e.typ === 'ende' ? 'Flug beenden' : 'Nachflug-Angaben'
  const zeit = zeitText(e.typ === 'start' ? e.payload.startzeit : e.typ === 'ende' ? e.daten.endzeit : undefined) || zeitText(e.erfasstAm)
  const teile = [drohne ? `Drohne ${drohne}` : (e.typ !== 'start' && e.ref.id !== undefined ? `Flug #${e.ref.id}` : ''), zeit].filter(Boolean)
  return `${was} (${teile.join(', ')})`
}

/** qids der Aktion und aller von ihr abhängigen Aktionen (Ende/Update eines noch nicht gesendeten Starts) */
function abhaengige(e: QueueEintrag, liste: QueueEintrag[]): Set<string> {
  const q = new Set<string>([e.qid])
  if (e.typ === 'start') liste.forEach(x => { if (x.typ !== 'start' && x.ref.uuid === e.uuid) q.add(x.qid) })
  return q
}

/** Vom Nutzer ausgelöstes Verwerfen (mit allen abhängigen Aktionen) */
export function queueVerwerfen(qid: string) {
  const liste = lesen()
  const e = liste.find(x => x.qid === qid)
  if (!e) return
  const weg = abhaengige(e, liste)
  schreiben(liste.filter(x => !weg.has(x.qid)))
}

// ---------------------------------------------------------------------------
// Senden
// ---------------------------------------------------------------------------
let laeuft = false
const gesendetListeners = new Set<(anzahl: number) => void>()
export function beiGesendet(cb: (anzahl: number) => void): () => void { gesendetListeners.add(cb); return () => { gesendetListeners.delete(cb) } }

function refAufloesen(ref: FlugRef, uuidZuId: Map<string, number>): number | null {
  if (ref.id !== undefined) return ref.id
  if (ref.uuid && uuidZuId.has(ref.uuid)) return uuidZuId.get(ref.uuid)!
  return null
}

/** Sendet die Warteschlange in Reihenfolge. Bricht bei einem Verbindungsfehler ab (Rest bleibt erhalten). Liefert die Zahl gesendeter Aktionen. */
export async function queueSenden(aktuellerNutzerId?: number, sofort = false): Promise<number> {
  if (laeuft) return 0
  laeuft = true
  let gesendet = 0
  const uuidZuId = new Map<string, number>()
  try {
    // Reihenfolge einhalten: immer das erste Element; bei Erfolg/endgültiger Ablehnung entfernen, sonst anhalten
    for (;;) {
      const liste = lesen()
      const e = liste.find(x => aktuellerNutzerId === undefined || x.nutzerId === aktuellerNutzerId)
      if (!e) break
      if (!sofort && e.nichtVor && e.nichtVor > Date.now()) break // Backoff nach Serverfehler
      try {
        if (e.typ === 'start') {
          const r = await api.post('/fluege', { ...e.payload, client_uuid: e.uuid }, { timeout: TIMEOUT_MS })
          const id = (r.data as { id?: number })?.id
          if (typeof id === 'number') {
            uuidZuId.set(e.uuid, id)
            // spätere Aktionen zu diesem Flug zeigen ab jetzt auf die echte id (überlebt auch einen Abbruch in der Runde)
            schreiben(lesen().map(x => (x.typ !== 'start' && x.ref.uuid === e.uuid ? { ...x, ref: { ...x.ref, id } } : x)))
          }
        } else {
          const id = refAufloesen(e.ref, uuidZuId)
          if (id === null) {
            // Start war nicht in dieser Runde: per UUID nachschlagen (Start wurde früher gesendet)
            fehlerMerken(`${beschreibe(e, liste)} konnte keinem Flug zugeordnet werden und wurde entfernt – bitte manuell nachtragen.`)
            schreiben(lesen().filter(x => x.qid !== e.qid))
            continue
          }
          if (e.typ === 'ende') await api.put(`/fluege/${id}/ende`, e.daten, { timeout: TIMEOUT_MS })
          else await api.put(`/fluege/${id}`, e.daten, { timeout: TIMEOUT_MS })
        }
        schreiben(lesen().filter(x => x.qid !== e.qid))
        gesendet++
      } catch (err: any) {
        if (istVerbindungsfehler(err)) break // später erneut
        const status: number | undefined = err?.response?.status
        const detail = err?.response?.data?.detail
        const grund = typeof detail === 'string' ? detail : 'Fehler ' + (status ?? '')
        if (istEndgueltigAbgelehnt(err)) {
          // Eindeutige Ablehnung (z. B. 400/409/422): Eintrag entfernen, aber sichtbar mit Inhalt melden, damit der Nutzer ihn manuell nachträgt
          fehlerMerken(`${beschreibe(e, liste)} wurde vom Server abgelehnt (${grund}) und aus der Warteschlange entfernt – bitte manuell nachtragen.`)
          schreiben(lesen().filter(x => !abhaengige(e, liste).has(x.qid)))
          continue
        }
        // 500/401/403/sonstiges: NICHT verwerfen. Eintrag bleibt, wird mit Backoff erneut gesendet und rot angezeigt.
        const versuche = (e.versuche ?? 0) + 1
        const wartezeit = status === 401 ? 5000 : Math.min(5 * 60_000, 5000 * 2 ** Math.min(versuche, 6))
        const text = status === 401 ? 'Anmeldung wird erneuert' : `Serverfehler (${grund})`
        schreiben(lesen().map(x => (x.qid === e.qid ? { ...x, versuche, nichtVor: Date.now() + wartezeit, letzterFehler: text } : x)))
        break // Reihenfolge einhalten: nachfolgende Aktionen warten
      }
    }
  } finally {
    laeuft = false
  }
  if (gesendet > 0) gesendetListeners.forEach(cb => cb(gesendet))
  return gesendet
}
